import React, { useEffect, useState } from 'react';
import { AnimatePresence, animate, motion } from 'motion/react';
import { EASE_IN_OUT } from '../../lib/motion';
import { useIntro } from '../../context/IntroContext';
import { useScrollLock } from './SmoothScroll';

const DURATION = 1.15;

// One digit column (0–9 stacked) that rolls to its value
const Digit = ({ value }) => (
  <span className="relative inline-block h-[1em] w-[0.62em] overflow-hidden">
    <span className="absolute inset-x-0 top-0 flex flex-col transition-transform duration-150 ease-out" style={{ transform: `translateY(${-value}em)` }}>
      {Array.from({ length: 10 }, (_, d) => (
        <span key={d} className="h-[1em] leading-none">{d}</span>
      ))}
    </span>
  </span>
);

/*
 * First-visit intro (once per session): on an ink screen the DVS monogram fills with brand
 * teal while a giant rolling counter climbs to 100, then the screen lifts away. The page
 * renders underneath the whole time; IntroContext tells the hero when to start its reveal.
 */
const Preloader = () => {
  const { ready, finish } = useIntro();
  const [count, setCount] = useState(0);
  const [visible, setVisible] = useState(!ready);

  useEffect(() => {
    if (ready) return undefined;
    const controls = animate(0, 100, {
      duration: DURATION,
      ease: [0.65, 0, 0.35, 1],
      onUpdate: v => setCount(Math.round(v)),
      onComplete: () => {
        finish();
        setVisible(false);
      }
    });
    return () => controls.stop();
  }, [ready, finish]);

  useScrollLock(visible);

  const digits = String(count).padStart(3, '0').split('').map(Number);
  const mask = {
    WebkitMaskImage: 'url(/brand/mark-dark.png)',
    maskImage: 'url(/brand/mark-dark.png)',
    WebkitMaskSize: 'contain',
    maskSize: 'contain',
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center',
    maskPosition: 'center'
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="preloader"
          aria-hidden="true"
          data-tone="ink"
          className="theme-ink fixed inset-0 z-[95] flex flex-col justify-between bg-background px-[var(--gutter)] py-8"
          exit={{ clipPath: 'inset(0 0 100% 0)', transition: { duration: 0.8, ease: EASE_IN_OUT } }}
          style={{ clipPath: 'inset(0 0 0% 0)' }}
        >
          <div className="flex justify-between">
            <span className="label">Dreamvision Solutions</span>
            <span className="label">Loading</span>
          </div>

          <div className="relative mx-auto h-[96px] w-[205px]">
            <div className="absolute inset-0 bg-foreground/[0.12]" style={mask} />
            <div
              className="absolute inset-0 bg-gradient-to-r from-brand to-brand-2"
              style={{ ...mask, clipPath: `inset(${100 - count}% 0 0 0)` }}
            />
          </div>

          <div className="flex items-end justify-between">
            <span className="label">Mumbai, India</span>
            <span className="flex font-mono text-[clamp(4rem,12vw,10rem)] font-light leading-none tracking-[-0.06em] text-foreground tabular">
              {digits.map((d, i) => <Digit key={i} value={d} />)}
            </span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Preloader;
